// Listing modes for the ADs and SBs register — List / Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. This file
// holds ONLY the vocabulary, deliberately separated from the layout that
// renders it, because this module is mounted TWO ways and both need the same
// segments, labels, storage key and validator:
//
//   1. standalone  /ads-and-sbs/<mode>            → ADsAndSBsLayout
//   2. nested      /aircraft_templates/ads-and-sbs/<mode>
//                                                 → AircraftTemplatesLayout
//
// Blank keeps its equivalents inside BlankLayout.tsx (the single-mount case).
// Extracting them is the smallest deviation that avoids two copies of the
// segment list drifting apart — which is the failure mode PATTERNS.md records
// for the seven hand-rolled STORAGE_KEY declarations, and the reason
// src/opportunities/modes.ts exists as its own file too.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "Compliance Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Directive register",
  process: "Compliance KPIs and intake",
};

/**
 * List, not Process. The register IS the working surface — an operator opens
 * this module to find a directive, and Process is the read-at-a-glance view.
 * That is also the honest default while compliance has no source: Process
 * currently shows one established figure and three em dashes, so landing there
 * first would present the module as emptier than it is.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<module>.viewMode`, underscored even though the directory is hyphenated —
 * the convention routeModeSegments.test.tsx pins for tech_log.
 * This exact key was already asserted (as necessarily ABSENT) by the previous
 * ADsAndSBsList suite, which anticipated this change.
 */
export const MODE_STORAGE_KEY = "ads_and_sbs.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION, deliberately — and that is a measured omission,
 * not an oversight. src/opportunities/modes.ts carries one because its key
 * previously held a bare mode NAME under a switcher that has since moved to
 * paths, so a returning user had a value in the old shape. This key has never
 * been written by anything: the module had exactly one view, and the suite that
 * covered it asserted `ads_and_sbs.viewMode` was NULL. There is no old shape to
 * accept. Anything already stored under this key is therefore not ours, and
 * falling through to the default is the right handling for it.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 *
 * Shared by both layouts so the two mounts derive the base identically. The
 * base is derived from the LIVE pathname rather than hardcoded because this
 * module is mounted at four paths (`/ads_and_sbs`, `/ads-and-sbs`,
 * `/aircraft_templates/ads-and-sbs`, and whatever nav row 215 resolves to) —
 * a hardcoded base would pass at the canonical path and break at the aliases,
 * which is exactly what routeModeSegments.test.tsx mounts non-canonically to
 * catch.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
